/* eslint-disable @typescript-eslint/no-explicit-any */
import api from './axiosConfig';

// Interfaces
export interface HVARiskCategory {
  id: number;
  name: string;
  displayorder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface HVARiskElement {
  id: number;
  categoryid: number;
  name: string;
  description?: string;
  aiprompt: string;
  displayorder: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  hvariskcategory?: HVARiskCategory;
}

export interface HVARiskCall {
  id: number;
  callid: string;
  riskelementid: number;
  iaanalysis: {
    severity: number;
    evidence: string;
    timestamp?: number;
    confidence: number;
    reasoning: string;
  };
  createdAt: string;
  updatedAt: string;
  hvariskelement?: HVARiskElement;
}

export interface HVARiskCallValue {
  id: number;
  callid: string;
  riskelementid: number;
  iariskvalue: number;
  active: boolean;
  qariskvalue?: number;
  qaagent?: number;
  qaagentcoment?: string;
  iariskvaluedate: string;
  qariskvaluedate?: string;
  hvariskelement?: HVARiskElement;
  reviewer?: {
    name: string;
    lastname: string;
  };
}

export interface RiskStatistics {
  totalRisks: number;
  highSeverityRisks: number;
  pendingReviews: number;
  reviewedRisks: number;
  avgRiskScore: number;
  avgReviewTime?: number;
  categoryName?: string;
}

export interface PendingRiskReview {
  total: number;
  risks: HVARiskCallValue[];
}

export interface RiskReview {
  riskelementid: number;
  qariskvalue: number;
  qaagentcoment?: string;
}

export interface RiskEvolutionData {
  date: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  veryLow?: number;
  total: number;
}

export interface RiskCategoryStats {
  categoryId: number;
  categoryName: string;
  totalRisks: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  pendingReviews: number;
  avgReviewTime?: number; // en minutos
}

export interface RiskHeatmapData {
  campaignId: number;
  campaignName: string;
  categoryId: number;
  categoryName: string;
  riskCount: number;
  avgSeverity: number;
}

export interface TopRiskElement {
  id: number;
  name: string;
  categoryName: string;
  count: number;
  avgSeverity: string;
  trend: 'up' | 'down' | 'stable';
  changePercent: number;
  rank: number;
}

export interface RiskDashboardSummary {
  totalRisks: number;
  criticalRisks: number;
  highRisks: number;
  mediumRisks: number;
  lowRisks: number;
  pendingReviews: number;
  reviewedToday: number;
  avgRiskScore: number;
  avgReviewTime: number; // en minutos
  trendsVsPrevPeriod: {
    totalChange: number; // porcentaje
    criticalChange: number;
    pendingChange: number;
  };
}

export interface RiskHeatmapData {
  campaignId: number;
  campaignName: string;
  elementId: number;
  elementName: string;
  categoryId: number;
  categoryName: string;
  riskCount: number;
  avgSeverity: number;
  criticalCount: number;
  highCount: number;
  severityDistribution: {
    critical: number;
    high: number;
  };
}

// API Functions
// Obtener todas las categorías de riesgo
export const fetchHVARiskCategories = async (): Promise<HVARiskCategory[]> => {
  try {
    const response = await api.get<HVARiskCategory[]>(
      'riskmanagement/categories'
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk categories:', error);
    throw error;
  }
};

// Obtener elementos de riesgo por categoría
export const fetchHVARiskElementsByCategory = async (
  categoryId: number
): Promise<HVARiskElement[]> => {
  try {
    const response = await api.get<HVARiskElement[]>(
      `riskmanagement/elements/${categoryId}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk elements by category:', error);
    throw error;
  }
};

// Obtener todos los elementos de riesgo
export const fetchHVARiskElements = async (): Promise<HVARiskElement[]> => {
  try {
    const response = await api.get<HVARiskElement[]>('riskmanagement/elements');
    return response.data;
  } catch (error) {
    console.error('Error fetching risk elements:', error);
    throw error;
  }
};

// Obtener riesgos detectados para una llamada
export const fetchCallRisks = async (
  callid: string,
  campaignid?: number
): Promise<HVARiskCall[]> => {
  try {
    const response = await api.post<HVARiskCall[]>('riskmanagement/callrisks', {
      callid,
      campaignid
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching call risks:', error);
    throw error;
  }
};

// Obtener valores de riesgo para revisión
export const fetchCallRiskValues = async (
  callid: string,
  campaignid?: number
): Promise<HVARiskCallValue[]> => {
  try {
    const response = await api.post<HVARiskCallValue[]>(
      'riskmanagement/callriskvalues',
      {
        callid,
        campaignid
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching call risk values:', error);
    throw error;
  }
};

// Revisar riesgos de una llamada
export const reviewCallRisks = async (
  callid: string,
  risks: RiskReview[]
): Promise<void> => {
  try {
    await api.post('riskmanagement/reviewrisks', {
      callid,
      risks
    });
  } catch (error) {
    console.error('Error reviewing call risks:', error);
    throw error;
  }
};

// Obtener estadísticas de riesgos
export const fetchRiskStatistics = async (params: {
  campaignid?: number;
  datefrom?: string;
  dateto?: string;
  serviceid?: number;
  categoryid?: number;
  riskelementid?: number;
}): Promise<RiskStatistics[]> => {
  try {
    const response = await api.post<RiskStatistics[]>(
      'riskmanagement/statistics',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk statistics:', error);
    throw error;
  }
};

// Obtener llamadas con riesgos pendientes de revisión
export const fetchPendingRiskReviews = async (params: {
  campaignid?: number;
  limit?: number;
  offset?: number;
}): Promise<PendingRiskReview> => {
  try {
    const response = await api.post<PendingRiskReview>(
      'riskmanagement/pendingreview',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching pending risk reviews:', error);
    throw error;
  }
};

// Funciones de administración (solo admin)
// Crear elemento de riesgo
export const createRiskElement = async (
  element: Partial<HVARiskElement>
): Promise<HVARiskElement> => {
  try {
    const response = await api.post<HVARiskElement>(
      'riskmanagement/element',
      element
    );
    return response.data;
  } catch (error) {
    console.error('Error creating risk element:', error);
    throw error;
  }
};

// Actualizar elemento de riesgo
export const updateRiskElement = async (
  id: number,
  element: Partial<HVARiskElement>
): Promise<HVARiskElement> => {
  try {
    const response = await api.put<HVARiskElement>(
      `riskmanagement/element/${id}`,
      element
    );
    return response.data;
  } catch (error) {
    console.error('Error updating risk element:', error);
    throw error;
  }
};

// Eliminar (desactivar) elemento de riesgo
export const deleteRiskElement = async (id: number): Promise<void> => {
  try {
    await api.delete(`riskmanagement/element/${id}`);
  } catch (error) {
    console.error('Error deleting risk element:', error);
    throw error;
  }
};

// Crear categoría de riesgo
export const createRiskCategory = async (
  category: Partial<HVARiskCategory>
): Promise<HVARiskCategory> => {
  try {
    const response = await api.post<HVARiskCategory>(
      'riskmanagement/category',
      category
    );
    return response.data;
  } catch (error) {
    console.error('Error creating risk category:', error);
    throw error;
  }
};

// Actualizar categoría de riesgo
export const updateRiskCategory = async (
  id: number,
  category: Partial<HVARiskCategory>
): Promise<HVARiskCategory> => {
  try {
    const response = await api.put<HVARiskCategory>(
      `riskmanagement/category/${id}`,
      category
    );
    return response.data;
  } catch (error) {
    console.error('Error updating risk category:', error);
    throw error;
  }
};

// Eliminar categoría de riesgo
export const deleteRiskCategory = async (id: number): Promise<void> => {
  try {
    await api.delete(`riskmanagement/category/${id}`);
  } catch (error) {
    console.error('Error deleting risk category:', error);
    throw error;
  }
};

// Obtener resumen del dashboard principal
export const fetchRiskDashboardSummary = async (params: {
  datefrom?: string;
  dateto?: string;
  campaignid?: number;
  serviceid?: number;
  categoryid?: number;
  riskelementid?: number;
}): Promise<RiskDashboardSummary> => {
  try {
    const response = await api.post<RiskDashboardSummary>(
      'riskmanagement/dashboard/summary',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk dashboard summary:', error);
    throw error;
  }
};

// Obtener estadísticas por categoría
export const fetchRiskCategoryStats = async (params: {
  datefrom?: string;
  dateto?: string;
  campaignid?: number;
  serviceid?: number;
  riskelementid?: number;
}): Promise<RiskCategoryStats[]> => {
  try {
    const response = await api.post<RiskCategoryStats[]>(
      'riskmanagement/dashboard/categorystats',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk category stats:', error);
    throw error;
  }
};

// Obtener riesgos para revisión con filtros avanzados
export const fetchFilteredRisksForReview = async (params: {
  campaignid?: number;
  categoryid?: number;
  severity?: number;
  status?: 'pending' | 'reviewed' | 'all';
  datefrom?: string;
  dateto?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'date' | 'severity' | 'category';
  sortOrder?: 'asc' | 'desc';
}): Promise<{
  total: number;
  risks: HVARiskCallValue[];
  stats: {
    pendingCount: number;
    reviewedCount: number;
    avgReviewTime: number;
  };
}> => {
  try {
    const response = await api.post<{
      total: number;
      risks: HVARiskCallValue[];
      stats: {
        pendingCount: number;
        reviewedCount: number;
        avgReviewTime: number;
      };
    }>('riskmanagement/review/filtered', params);
    return response.data;
  } catch (error) {
    console.error('Error fetching filtered risks for review:', error);
    throw error;
  }
};

// Obtener métricas de rendimiento del equipo QA
export const fetchQATeamMetrics = async (params: {
  datefrom?: string;
  dateto?: string;
  campaignid?: number;
}): Promise<{
  agentMetrics: Array<{
    agentId: number;
    agentName: string;
    reviewedCount: number;
    avgReviewTime: number;
    accuracyRate: number; // comparación con IA
  }>;
  overallMetrics: {
    totalReviewed: number;
    avgReviewTime: number;
    overturnRate: number; // % de cambios respecto a IA
  };
}> => {
  try {
    const response = await api.post<any>(
      'riskmanagement/metrics/qateam',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching QA team metrics:', error);
    throw error;
  }
};

// Revisar múltiples riesgos (bulk review)
export const bulkReviewRisks = async (
  reviews: Array<{
    callid: string;
    riskelementid: number;
    qariskvalue: number;
    qaagentcoment?: string;
  }>
): Promise<{
  success: boolean;
  reviewedCount: number;
  errors?: string[];
}> => {
  try {
    const response = await api.post<any>('riskmanagement/review/bulk', {
      reviews
    });
    return response.data;
  } catch (error) {
    console.error('Error in bulk risk review:', error);
    throw error;
  }
};

// Exportar datos de riesgos
export const exportRiskData = async (params: {
  format: 'csv' | 'excel' | 'pdf';
  filters: {
    campaignid?: number;
    categoryid?: number;
    severity?: number;
    status?: string;
    datefrom?: string;
    dateto?: string;
    serviceid?: number;
    riskelementid?: number;
  };
  columns?: string[]; // columnas específicas a exportar
}): Promise<Blob> => {
  try {
    const response = await api.post('riskmanagement/export', params, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting risk data:', error);
    throw error;
  }
};

// Obtener distribución de severidad para gráfico de pie/donut
export const fetchSeverityDistribution = async (params: {
  datefrom?: string;
  dateto?: string;
  campaignid?: number;
  categoryid?: number;
}): Promise<
  Array<{
    severity: number;
    severityLabel: string;
    count: number;
    percentage: number;
  }>
> => {
  try {
    const response = await api.post<any>(
      'riskmanagement/dashboard/severitydistribution',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching severity distribution:', error);
    throw error;
  }
};

// Guardar configuración de dashboard del usuario
export const saveUserDashboardConfig = async (config: {
  widgets: string[];
  layout: any;
  filters: any;
}): Promise<void> => {
  try {
    await api.post('riskmanagement/dashboard/userconfig', config);
  } catch (error) {
    console.error('Error saving dashboard config:', error);
    throw error;
  }
};

// Obtener configuración de dashboard del usuario
export const fetchUserDashboardConfig = async (): Promise<{
  widgets: string[];
  layout: any;
  filters: any;
} | null> => {
  try {
    const response = await api.get('riskmanagement/dashboard/userconfig');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard config:', error);
    return null;
  }
};

// Obtener detalles de una llamada
export const fetchCallDetails = async (callid: string): Promise<any> => {
  try {
    const response = await api.get(`riskmanagement/call/${callid}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching call details:', error);
    throw error;
  }
};

// Actualizar la función fetchRiskEvolution para usar el endpoint correcto
export const fetchRiskEvolution = async (params: {
  datefrom: string;
  dateto: string;
  campaignid?: number;
  interval?: 'daily' | 'weekly' | 'monthly';
  serviceid?: number;
  categoryid?: number;
  riskelementid?: number;
}): Promise<RiskEvolutionData[]> => {
  try {
    const response = await api.post<RiskEvolutionData[]>(
      'riskmanagement/dashboard/evolution',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk evolution data:', error);
    throw error;
  }
};

// Actualizar fetchRiskAlerts para usar el endpoint correcto
export const fetchRiskAlerts = async (params?: {
  severity?: number;
  limit?: number;
  unreviewed?: boolean;
}): Promise<HVARiskCallValue[]> => {
  // <-- Cambiar aquí el tipo
  try {
    const response = await api.get<HVARiskCallValue[]>(
      'riskmanagement/alerts',
      {
        params
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk alerts:', error);
    throw error;
  }
};

// Obtener top elementos de riesgo
export const fetchTopRiskElements = async (params: {
  datefrom: string;
  dateto: string;
  campaignid?: number;
  categoryid?: number;
  serviceid?: number;
  limit?: number;
}): Promise<TopRiskElement[]> => {
  try {
    const response = await api.post<TopRiskElement[]>(
      'riskmanagement/dashboard/topriskelements',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching top risk elements:', error);
    throw error;
  }
};

// Obtener datos para heatmap
export const fetchRiskHeatmapData = async (params: {
  datefrom: string;
  dateto: string;
  serviceid?: number;
  campaignid?: number;
  categoryid?: number;
}): Promise<RiskHeatmapData[]> => {
  try {
    const response = await api.post<RiskHeatmapData[]>(
      'riskmanagement/dashboard/heatmap',
      params
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching risk heatmap data:', error);
    throw error;
  }
};
